// src/utils/computeMetrics.js
// Derives weekly summary records (the existing {weekEnding, fields:{}} shape)
// from raw transaction collections.
// The dashboard, analytics and reports pages consume these derived records unchanged.

/**
 * Given a transactions object, returns an array of weekly summary records
 * sorted newest-first, matching the existing SCMRecord shape.
 */
export function computeWeeklyRecords(transactions) {
  const {
    contracts = [],
    purchaseOrders = [],
    cscMemos = [],
    tenderBoardItems = [],
    suppliers = [],
    rfxItems = [],
    stakeholderEngagements = [],
    dieselRecords = [],
    logisticsRecords = [],
    projectUpdates = [],
    processNotes = [],
    costSavings = [],
  } = transactions;

  // Collect every week that appears in any collection
  const weekSet = new Set([
    ...costSavings.map((t) => t.weekEnding),
    ...contracts.map((t) => t.weekEnding),
    ...purchaseOrders.map((t) => t.weekEnding),
    ...cscMemos.map((t) => t.weekEnding),
    ...tenderBoardItems.map((t) => t.weekEnding),
    ...suppliers.map((t) => t.weekEnding),
    ...rfxItems.map((t) => t.weekEnding),
    ...stakeholderEngagements.map((t) => t.weekEnding),
    ...dieselRecords.map((t) => t.weekEnding),
    ...logisticsRecords.map((t) => t.weekEnding),
    ...projectUpdates.map((t) => t.weekEnding),
    ...processNotes.map((t) => t.weekEnding),
  ]);

  const weeks = Array.from(weekSet).sort((a, b) => b.localeCompare(a));

  return weeks.map((weekEnding) => {
    const wcs  = costSavings.filter((t) => t.weekEnding === weekEnding);
    const wc   = contracts.filter((t) => t.weekEnding === weekEnding);
    const wpo  = purchaseOrders.filter((t) => t.weekEnding === weekEnding);
    const wcsc = cscMemos.filter((t) => t.weekEnding === weekEnding);
    const wtb  = tenderBoardItems.filter((t) => t.weekEnding === weekEnding);
    const wsup = suppliers.filter((t) => t.weekEnding === weekEnding);
    const wrfx = rfxItems.filter((t) => t.weekEnding === weekEnding);
    const wse  = stakeholderEngagements.filter((t) => t.weekEnding === weekEnding);

    const diesel    = dieselRecords.find((t) => t.weekEnding === weekEnding);
    const logistics = logisticsRecords.find((t) => t.weekEnding === weekEnding);
    const wproj     = projectUpdates.filter((t) => t.weekEnding === weekEnding);
    const wNote     = processNotes.find((t) => t.weekEnding === weekEnding);

    // PO avg cycle time — only average POs that have a cycleTime
    const posWithCycle = wpo.filter((p) => p.cycleTime != null);
    const avgCycle = posWithCycle.length
      ? posWithCycle.reduce((s, p) => s + p.cycleTime, 0) / posWithCycle.length
      : null;

    const poSpendTotal = wpo.reduce((s, p) => s + (p.amountUSD || 0), 0);

    const minorTB = wtb.filter((t) => t.type === "Minor").reduce((s, t) => s + (t.amount || 0), 0);
    const majorTB = wtb.filter((t) => t.type === "Major").reduce((s, t) => s + (t.amount || 0), 0);

    return {
      id: `w-${weekEnding}`,
      weekEnding,
      fields: {
        contractsExecuted:              wc.filter((c) => c.type === "executed").length || null,
        contractsAwarded:               wc.filter((c) => c.type === "awarded").length || null,
        suppliersOnboarded:             wsup.length || null,
        rfxIssued:                      wrfx.length || null,
        supplierPerformanceMonitoring:  null, // removed
        stakeholderEngagement:          wse.length || null,
        poCount:                        wpo.length || null,
        poReceipted:                    wpo.filter((p) => p.status === "receipted").length || null,
        poAvgCycleTime:                 avgCycle,
        poSpend:                        poSpendTotal || null,
        amountMinorTB:                  minorTB || null,
        amountMajorTB:                  majorTB || null,
        cscMemos:                       wcsc.length || null,
        dieselOpeningStock:             diesel?.openingStock ?? null,
        dieselSupply:                   diesel?.supply ?? null,
        dieselConsumption:              diesel?.consumption ?? null,
        dieselClosingStock:             diesel?.closingStock ?? null,
        uyoLogisticsTrips:              logistics?.trips ?? null,
        uyoLogisticsSpend:              logistics?.spend ?? null,
        uyoPortacabins:                 wproj.find((p) => p.key === "uyoPortacabins")?.progress ?? null,
        uyoBaseProgress:                wproj.find((p) => p.key === "uyoBaseProgress")?.progress ?? null,
        cateringProgress:               wproj.find((p) => p.key === "cateringProgress")?.progress ?? null,
        processImprovement:             wNote?.text || null,
        highlights:                     wNote?.highlights || null,
        totalCostSavings:               wcs.reduce((s, r) => s + (r.savingsAmount || 0), 0) || null,
        costSavingsCount:               wcs.length || null,
      },
    };
  });
}

// ─── SP Weekly Detail ─────────────────────────────────────────────────────────
// When in live SharePoint mode the data source only has weekly aggregates,
// not individual transactions. This shows the relevant aggregate fields per
// week so the user can still see what's behind each KPI.

const SP_DETAIL_MAP = {
  contractsExecuted:             { label: "Contracts (Weekly)", fields: [{ key: "contractsExecuted", label: "Executed", type: "number" }, { key: "contractsAwarded", label: "Awarded", type: "number" }] },
  contractsAwarded:              { label: "Contracts (Weekly)", fields: [{ key: "contractsExecuted", label: "Executed", type: "number" }, { key: "contractsAwarded", label: "Awarded", type: "number" }] },
  poCount:                       { label: "Purchase Orders (Weekly)", fields: [{ key: "poCount", label: "Total POs", type: "number" }, { key: "poReceipted", label: "Receipted", type: "number" }, { key: "poSpend", label: "Spend ($)", type: "currency" }, { key: "poAvgCycleTime", label: "Avg Cycle (days)", type: "number" }] },
  poReceipted:                   { label: "Purchase Orders (Weekly)", fields: [{ key: "poCount", label: "Total POs", type: "number" }, { key: "poReceipted", label: "Receipted", type: "number" }, { key: "poSpend", label: "Spend ($)", type: "currency" }, { key: "poAvgCycleTime", label: "Avg Cycle (days)", type: "number" }] },
  poSpend:                       { label: "Purchase Orders (Weekly)", fields: [{ key: "poCount", label: "Total POs", type: "number" }, { key: "poReceipted", label: "Receipted", type: "number" }, { key: "poSpend", label: "Spend ($)", type: "currency" }, { key: "poAvgCycleTime", label: "Avg Cycle (days)", type: "number" }] },
  poAvgCycleTime:                { label: "Purchase Orders (Weekly)", fields: [{ key: "poCount", label: "Total POs", type: "number" }, { key: "poReceipted", label: "Receipted", type: "number" }, { key: "poSpend", label: "Spend ($)", type: "currency" }, { key: "poAvgCycleTime", label: "Avg Cycle (days)", type: "number" }] },
  cscMemos:                      { label: "CSC Memos (Weekly)", fields: [{ key: "cscMemos", label: "CSC Memos", type: "number" }] },
  rfxIssued:                     { label: "RFX Issued (Weekly)", fields: [{ key: "rfxIssued", label: "RFX Issued", type: "number" }] },
  suppliersOnboarded:            { label: "Suppliers (Weekly)", fields: [{ key: "suppliersOnboarded", label: "Onboarded", type: "number" }] },
  stakeholderEngagement:         { label: "Stakeholder Engagements (Weekly)", fields: [{ key: "stakeholderEngagement", label: "Engagements", type: "number" }] },
  supplierPerformanceMonitoring: { label: "Supplier Performance (Weekly)", fields: [{ key: "supplierPerformanceMonitoring", label: "Reviews", type: "number" }] },
  amountMinorTB:                 { label: "Minor Tender Board (Weekly)", fields: [{ key: "amountMinorTB", label: "Minor TB ($)", type: "currency" }] },
  amountMajorTB:                 { label: "Major Tender Board (Weekly)", fields: [{ key: "amountMajorTB", label: "Major TB ($)", type: "currency" }] },
  dieselOpeningStock:            { label: "Diesel Inventory (Weekly)", fields: [{ key: "dieselOpeningStock", label: "Opening (L)", type: "number" }, { key: "dieselSupply", label: "Supply (L)", type: "number" }, { key: "dieselConsumption", label: "Consumption (L)", type: "number" }, { key: "dieselClosingStock", label: "Closing (L)", type: "number" }] },
  dieselSupply:                  { label: "Diesel Inventory (Weekly)", fields: [{ key: "dieselOpeningStock", label: "Opening (L)", type: "number" }, { key: "dieselSupply", label: "Supply (L)", type: "number" }, { key: "dieselConsumption", label: "Consumption (L)", type: "number" }, { key: "dieselClosingStock", label: "Closing (L)", type: "number" }] },
  dieselConsumption:             { label: "Diesel Inventory (Weekly)", fields: [{ key: "dieselOpeningStock", label: "Opening (L)", type: "number" }, { key: "dieselSupply", label: "Supply (L)", type: "number" }, { key: "dieselConsumption", label: "Consumption (L)", type: "number" }, { key: "dieselClosingStock", label: "Closing (L)", type: "number" }] },
  dieselClosingStock:            { label: "Diesel Inventory (Weekly)", fields: [{ key: "dieselOpeningStock", label: "Opening (L)", type: "number" }, { key: "dieselSupply", label: "Supply (L)", type: "number" }, { key: "dieselConsumption", label: "Consumption (L)", type: "number" }, { key: "dieselClosingStock", label: "Closing (L)", type: "number" }] },
  uyoLogisticsTrips:             { label: "Logistics (Weekly)", fields: [{ key: "uyoLogisticsTrips", label: "Trips", type: "number" }, { key: "uyoLogisticsSpend", label: "Spend (₦)", type: "currency" }] },
  uyoLogisticsSpend:             { label: "Logistics (Weekly)", fields: [{ key: "uyoLogisticsTrips", label: "Trips", type: "number" }, { key: "uyoLogisticsSpend", label: "Spend (₦)", type: "currency" }] },
  uyoPortacabins:                { label: "Project Progress (Weekly)", fields: [{ key: "uyoPortacabins", label: "Uyo Portacabins %", type: "percentage" }, { key: "uyoBaseProgress", label: "Uyo Base %", type: "percentage" }, { key: "cateringProgress", label: "Catering %", type: "percentage" }] },
  uyoBaseProgress:               { label: "Project Progress (Weekly)", fields: [{ key: "uyoPortacabins", label: "Uyo Portacabins %", type: "percentage" }, { key: "uyoBaseProgress", label: "Uyo Base %", type: "percentage" }, { key: "cateringProgress", label: "Catering %", type: "percentage" }] },
  cateringProgress:              { label: "Project Progress (Weekly)", fields: [{ key: "uyoPortacabins", label: "Uyo Portacabins %", type: "percentage" }, { key: "uyoBaseProgress", label: "Uyo Base %", type: "percentage" }, { key: "cateringProgress", label: "Catering %", type: "percentage" }] },
  totalCostSavings:              { label: "Cost Savings (Weekly)", fields: [{ key: "totalCostSavings", label: "Total Savings ($)", type: "currency" }, { key: "costSavingsCount", label: "No. of Savings", type: "number" }] },
  costSavingsCount:              { label: "Cost Savings (Weekly)", fields: [{ key: "totalCostSavings", label: "Total Savings ($)", type: "currency" }, { key: "costSavingsCount", label: "No. of Savings", type: "number" }] },
};

export function getDetailFromSP(spRecords, metricKey) {
  const config = SP_DETAIL_MAP[metricKey];
  if (!config) return { type: metricKey, columns: [], rows: [] };

  const weekEndingCol = { key: "weekEnding", label: "Week Ending", type: "date" };
  const columns = [weekEndingCol, ...config.fields];

  // Only include weeks that have at least one non-null value for this group
  const rows = spRecords
    .filter((r) => config.fields.some((f) => r.fields?.[f.key] != null))
    .map((r) => ({ id: r.id, weekEnding: r.weekEnding, ...r.fields }));

  return { type: config.label, columns, rows };
}

// ─── Detail Resolver ──────────────────────────────────────────────────────────
// Returns the underlying transaction records for a given metric key and
// optional week filter (pass weekEnding to limit to one week, or null for all).

export function getDetailRecords(transactions, metricKey, weekEnding = null) {
  const {
    contracts = [],
    purchaseOrders = [],
    cscMemos = [],
    tenderBoardItems = [],
    suppliers = [],
    rfxItems = [],
    stakeholderEngagements = [],
    dieselRecords = [],
    logisticsRecords = [],
    projectUpdates = [],
    // eslint-disable-next-line no-unused-vars
    processNotes = [],
    costSavings = [],
  } = transactions || {};

  const inWeek = (t) => !weekEnding || t.weekEnding === weekEnding;

  switch (metricKey) {
    case "contractsExecuted":
      return { type: "Contracts Executed", columns: contractCols, rows: contracts.filter((c) => c.type === "executed" && inWeek(c)) };
    case "contractsAwarded":
      return { type: "Contracts Awarded", columns: contractCols, rows: contracts.filter((c) => c.type === "awarded" && inWeek(c)) };
    case "suppliersOnboarded":
      return { type: "Suppliers Onboarded", columns: supplierCols, rows: suppliers.filter(inWeek) };
    case "rfxIssued":
      return { type: "RFX Issued", columns: rfxCols, rows: rfxItems.filter(inWeek) };
    case "supplierPerformanceMonitoring":
      return { type: "Supplier Performance", columns: [], rows: [] };
    case "totalCostSavings":
    case "costSavingsCount":
      return { type: "Cost Savings", columns: costSavingsCols, rows: costSavings.filter(inWeek) };
    case "stakeholderEngagement":
      return { type: "Stakeholder Engagements", columns: seCols, rows: stakeholderEngagements.filter(inWeek) };
    case "poCount":
    case "poReceipted":
    case "poAvgCycleTime":
    case "poSpend":
      return { type: "Purchase Orders", columns: poCols, rows: purchaseOrders.filter(inWeek) };
    case "amountMinorTB":
      return { type: "Minor Tender Board Items", columns: tbCols, rows: tenderBoardItems.filter((t) => t.type === "Minor" && inWeek(t)) };
    case "amountMajorTB":
      return { type: "Major Tender Board Items", columns: tbCols, rows: tenderBoardItems.filter((t) => t.type === "Major" && inWeek(t)) };
    case "cscMemos":
      return { type: "CSC Memos", columns: cscCols, rows: cscMemos.filter(inWeek) };
    case "dieselOpeningStock":
    case "dieselSupply":
    case "dieselConsumption":
    case "dieselClosingStock":
      return { type: "Diesel Records", columns: dieselCols, rows: dieselRecords.filter(inWeek) };
    case "uyoLogisticsTrips":
    case "uyoLogisticsSpend":
      return { type: "Logistics Records", columns: logisticsCols, rows: logisticsRecords.filter(inWeek) };
    case "uyoPortacabins":
    case "uyoBaseProgress":
    case "cateringProgress":
      return { type: "Project Updates", columns: projectCols, rows: projectUpdates.filter((p) => p.key === metricKey && inWeek(p)) };
    default:
      return { type: metricKey, columns: [], rows: [] };
  }
}

// ─── Column definitions for each transaction type ────────────────────────────
const contractCols = [
  { key: "date",     label: "Date",          type: "date" },
  { key: "entity",   label: "Entity",        type: "text" },
  { key: "title",    label: "Contract Title", type: "text" },
  { key: "vendor",   label: "Supplier",      type: "text" },
  { key: "value",    label: "Value ($)",     type: "currency" },
  { key: "type",     label: "Status",        type: "badge" },
];

const poCols = [
  { key: "date",        label: "Date",         type: "date" },
  { key: "poNumber",    label: "PO No.",       type: "text" },
  { key: "vendor",      label: "Supplier",     type: "text" },
  { key: "description", label: "Description",  type: "text" },
  { key: "amountUSD",   label: "Amount ($)",   type: "currency" },
  { key: "category",    label: "Category",     type: "text" },
  { key: "subcategory", label: "Subcategory",  type: "text" },
];

const cscCols = [
  { key: "date",    label: "Date",       type: "date" },
  { key: "entity",  label: "Entity",     type: "text" },
  { key: "title",   label: "Memo Title", type: "text" },
  { key: "value",   label: "Value ($)",  type: "currency" },
  { key: "status",  label: "Status",     type: "badge" },
  { key: "comments",label: "Comments",   type: "text" },
];

const tbCols = [
  { key: "date",         label: "Date",         type: "date" },
  { key: "entity",       label: "Entity",       type: "text" },
  { key: "tbNumber",     label: "TB No.",       type: "text" },
  { key: "title",        label: "Proposal",     type: "text" },
  { key: "supplier",     label: "Supplier",     type: "text" },
  { key: "amount",       label: "Amount ($)",   type: "currency" },
  { key: "proposalType", label: "Proposal Type", type: "badge" },
  { key: "type",         label: "TB Type",      type: "badge" },
  { key: "status",       label: "Status",       type: "badge" },
];

const supplierCols = [
  { key: "date",          label: "Date",         type: "date" },
  { key: "name",          label: "Supplier Name", type: "text" },
  { key: "category",      label: "Category",     type: "text" },
  { key: "contactPerson", label: "Contact",       type: "text" },
  { key: "status",        label: "Status",        type: "badge" },
];

const rfxCols = [
  { key: "date",   label: "Date",      type: "date" },
  { key: "entity", label: "Entity",    type: "text" },
  { key: "title",  label: "RFX Title", type: "text" },
  { key: "type",   label: "Type",      type: "badge" },
  { key: "status", label: "Status",    type: "badge" },
];

const seCols = [
  { key: "date",         label: "Date",        type: "date" },
  { key: "description",  label: "Description", type: "text" },
  { key: "stakeholder",  label: "Stakeholder", type: "text" },
  { key: "type",         label: "Type",        type: "badge" },
];

const dieselCols = [
  { key: "weekEnding",    label: "Week Ending",    type: "date" },
  { key: "openingStock",  label: "Opening (L)",    type: "number" },
  { key: "supply",        label: "Supply (L)",     type: "number" },
  { key: "consumption",   label: "Consumption (L)", type: "number" },
  { key: "closingStock",  label: "Closing (L)",    type: "number" },
  { key: "location",      label: "Location",       type: "text" },
];

const logisticsCols = [
  { key: "weekEnding", label: "Week Ending", type: "date" },
  { key: "route",      label: "Route",       type: "text" },
  { key: "trips",      label: "Trips",       type: "number" },
  { key: "spend",      label: "Spend (₦)",   type: "currency_ngn" },
  { key: "notes",      label: "Notes",       type: "text" },
];

const projectCols = [
  { key: "weekEnding",   label: "Week Ending",  type: "date" },
  { key: "projectName",  label: "Project",      type: "text" },
  { key: "progress",     label: "Progress",     type: "percentage" },
  { key: "notes",        label: "Notes",        type: "text" },
];

const costSavingsCols = [
  { key: "date",             label: "Date",              type: "date" },
  { key: "entity",           label: "Entity",            type: "text" },
  { key: "category",         label: "Category",          type: "text" },
  { key: "description",      label: "Description",       type: "text" },
  { key: "supplier",         label: "Supplier",          type: "text" },
  { key: "baselineCost",     label: "Baseline Cost ($)", type: "currency" },
  { key: "negotiatedCost",   label: "Negotiated Cost ($)",type: "currency" },
  { key: "savingsAmount",    label: "Savings ($)",       type: "currency" },
  { key: "savingsPercent",   label: "Savings %",         type: "percentage" },
  { key: "savingsFrequency", label: "Frequency",         type: "badge" },
  { key: "savingsType",      label: "Savings Type",      type: "badge" },
  { key: "notes",            label: "Notes",             type: "text" },
];
